/**
 * source1 (code): https://usehooks.com/page/2
 * source2 (types and ssr support): https://github.com/Andarist/use-onclickoutside/blob/master/src/index.ts
 */

import { useEffect } from 'react';

type EventType =
  | HTMLElementEventMap['mousedown']
  | HTMLElementEventMap['touchstart'];

type Handler = (event?: EventType) => void;

function useOnClickOutside(
  ref: React.RefObject<HTMLElement>,
  handler: Handler,
  extraArrDependencies?: Array<any>,
) {
  useEffect(() => {
    const listener = (event: EventType) => {
      // do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }

      handler(event);
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      }
    };
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
  }, [ref, handler, ...(extraArrDependencies || [])]);
}

export default useOnClickOutside;
